import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { IconButton } from '../../IconButton';
import { MONTH_NAMES } from '../constants';
import type { CalendarState, MonthState } from '../types';

type DateRangePickerCalendarProps = {
  calendarState: CalendarState;
  disabled?: boolean;
  disableFutureDays?: boolean;
};

type MonthVisualState =
  | 'selected'
  | 'previewSelected'
  | 'range'
  | 'previewRange'
  | 'default';

function resolveMonthVisualState(state: MonthState): MonthVisualState {
  if (state.isFromSelection || state.isToSelection) return 'selected';

  if (
    state.isPreview &&
    (state.isStart || state.isEnd) &&
    !state.isFromSelection &&
    !state.isToSelection
  )
    return 'previewSelected';

  if (state.isInRange) return state.isPreview ? 'previewRange' : 'range';

  return 'default';
}

export function DateRangePickerCalendar({
  calendarState,
  disabled,
  disableFutureDays = false,
}: DateRangePickerCalendarProps) {
  const {
    fromYear,
    toYear,
    prevFromYear,
    prevToYear,
    isAnimating,
    slideDirection,
    navigate,
    onMonthClick,
    onMonthHover,
    clearHoveredMonth,
    getMonthState,
  } = calendarState;

  const currentYear = NonTimezoneSensitiveDate.now().getYear();
  const shouldHideRightArrow =
    disableFutureDays && toYear >= currentYear;

  const renderMonthButtons = (year: number) => {
    const monthsData = MONTH_NAMES.map((monthName, index) => {
      const month = index + 1;
      return {
        month,
        monthName,
        state: getMonthState(year, month),
      };
    });

    const GRID_COLUMNS = 3;
    const GRID_ROW_HEIGHT_PX = 32;
    const GRID_COLUMN_GAP_PX = 4;
    const GRID_ROW_GAP_PX = 4;
    const GRID_TOTAL_COLUMN_GAP_PX = GRID_COLUMN_GAP_PX * (GRID_COLUMNS - 1);
    const GRID_TRACK_WIDTH_EXPRESSION = `(100% - ${GRID_TOTAL_COLUMN_GAP_PX}px) / ${GRID_COLUMNS}`;

    const rowSegments: {
      row: number;
      startCol: number;
      endCol: number;
      isPreview: boolean;
    }[] = [];

    for (let row = 0; row < 4; row += 1) {
      const rowStartIndex = row * 3;
      const rowMonths = monthsData.slice(rowStartIndex, rowStartIndex + 3);

      let firstInRow = -1;
      let lastInRow = -1;

      rowMonths.forEach(({ state }, idx) => {
        if (state.isInRange) {
          if (firstInRow === -1) {
            firstInRow = idx;
          }
          lastInRow = idx;
        }
      });

      if (firstInRow !== -1 && lastInRow !== -1) {
        const segmentMonths = rowMonths.slice(firstInRow, lastInRow + 1);
        const segmentIsPreview = segmentMonths.some(
          ({ state }) => state.isPreview
        );

        rowSegments.push({
          row,
          startCol: firstInRow,
          endCol: lastInRow,
          isPreview: segmentIsPreview,
        });
      }
    }

    return (
      <>
        {rowSegments.map(({ row, startCol, endCol, isPreview }) => (
          <div
            key={`segment-${row}-${startCol}-${endCol}`}
            className={`absolute rounded-md pointer-events-none z-0 transition-colors ${
              isPreview ? 'bg-bg-secondary' : 'bg-bg-secondary'
            }`}
            style={{
              top: `${row * (GRID_ROW_HEIGHT_PX + GRID_ROW_GAP_PX)}px`,
              height: `${GRID_ROW_HEIGHT_PX}px`,
              left: `calc(${startCol} * (${GRID_TRACK_WIDTH_EXPRESSION}) + ${
                startCol * GRID_COLUMN_GAP_PX
              }px)`,
              width: `calc(${endCol - startCol + 1} * (${GRID_TRACK_WIDTH_EXPRESSION}) + ${
                (endCol - startCol) * GRID_COLUMN_GAP_PX
              }px)`,
            }}
          />
        ))}
        {monthsData.map(({ month, monthName, state }) => {
          const isMonthDisabled = disabled || state.isDisabled;
          const enableHover = !isMonthDisabled;
          const visualState = resolveMonthVisualState(state);

          const classNames = [
            'relative z-10 h-8 py-sm px-md rounded-md txt-small-medium transition-colors border flex items-center justify-center',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          ];

          switch (visualState) {
            case 'selected':
              classNames.push('bg-fg-brand text-white border-white');
              if (enableHover)
                classNames.push(
                  'hover:bg-fg-brand-hover hover:text-white border-white'
                );
              break;
            case 'previewSelected':
              classNames.push(
                'bg-bg-tertiary text-text-secondary border-transparent'
              );
              if (enableHover)
                classNames.push(
                  'hover:bg-bg-tertiary hover:text-text-secondary-hover hover:border-transparent'
                );
              break;
            case 'range':
              classNames.push(
                'bg-transparent text-text-secondary border-transparent'
              );
              if (enableHover) {
                classNames.push(
                  'hover:bg-bg-tertiary hover:text-text-secondary hover:border-transparent'
                );
              }
              break;
            case 'previewRange':
              classNames.push(
                'bg-bg-secondary text-text-secondary border-transparent'
              );
              if (enableHover) {
                classNames.push(
                  'hover:bg-bg-secondary-hover hover:text-text-secondary hover:border-transparent'
                );
              }
              break;
            default:
              classNames.push(
                'bg-bg-white text-text-secondary border-transparent'
              );
              if (enableHover)
                classNames.push(
                  'hover:bg-bg-tertiary hover:text-text-secondary-hover hover:border-transparent'
                );
          }

          const onClick = () => onMonthClick(year, month, isMonthDisabled);

          const onHover = () => {
            if (!isMonthDisabled) onMonthHover(year, month);
          };

          return (
            <button
              key={month}
              type="button"
              onClick={onClick}
              onMouseEnter={onHover}
              onFocus={onHover}
              disabled={isMonthDisabled}
              className={classNames.join(' ')}
            >
              {monthName}
            </button>
          );
        })}
      </>
    );
  };

  const renderMonthGrid = (year: number) => (
    <div className="space-y-2">
      <div
        className="relative grid grid-cols-3 gap-1"
        onMouseLeave={clearHoveredMonth}
      >
        {renderMonthButtons(year)}
      </div>
    </div>
  );

  return (
    <div className="relative overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <div className="relative z-10">
          <IconButton
            icon={<ChevronLeft className="h-4 w-4" />}
            aria-label="Previous month"
            size="sm"
            onClick={() => navigate('prev')}
            disabled={disabled || isAnimating}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 flex-1 justify-center items-center pointer-events-none">
          <span className="txt-small-medium text-text-primary text-center -translate-x-[11px]">
            {fromYear}
          </span>
          <span className="txt-small-medium text-text-primary text-center translate-x-[11px]">
            {toYear}
          </span>
        </div>

        <div className="relative z-10">
          {!shouldHideRightArrow ? (
            <IconButton
              icon={<ChevronRight className="h-4 w-4" />}
              aria-label="Next month"
              size="sm"
              onClick={() => navigate('next')}
              disabled={disabled || isAnimating}
            />
          ) : (
            <div className="size-6" />
          )}
        </div>
      </div>

      <div className="relative overflow-hidden" style={{ height: '140px' }}>
        {isAnimating && prevFromYear !== null && prevToYear !== null ? (
          <>
            <div
              key={`old-${prevFromYear}-${prevToYear}`}
              className="grid grid-cols-2 gap-4 absolute inset-0"
              style={{
                animation:
                  slideDirection === 'left'
                    ? 'slideOutToLeft 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards'
                    : 'slideOutToRight 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards',
                zIndex: 1,
              }}
            >
              {renderMonthGrid(prevFromYear)}
              {renderMonthGrid(prevToYear)}
            </div>
            <div
              key={`new-${fromYear}-${toYear}`}
              className="grid grid-cols-2 gap-4 absolute inset-0"
              style={{
                animation:
                  slideDirection === 'left'
                    ? 'slideInFromRight 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards'
                    : 'slideInFromLeft 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards',
                zIndex: 2,
              }}
            >
              {renderMonthGrid(fromYear)}
              {renderMonthGrid(toYear)}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-4 absolute inset-0">
            {renderMonthGrid(fromYear)}
            {renderMonthGrid(toYear)}
          </div>
        )}
      </div>
    </div>
  );
}
